import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LONG_DISTANCE_ADVANTAGES, FREQUENT_DESTINATIONS, LONG_DISTANCE_VISUAL } from "@/constants/business";
import { useDialogs } from "@/context/DialogsContext";

export default function LongDistanceSection() {
  const { requestEstimate } = useDialogs();

  return (
    <section id="longue-distance" data-testid="long-distance-section" className="py-14 md:py-20 px-4 md:px-8 max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange">National &amp; régional</span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-navy mt-3 mb-4">
            Transport longue distance depuis Le Havre
          </h2>
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-6">
            Express Delivery organise des transports depuis Le Havre et la Normandie vers les autres régions françaises. Chaque mission est étudiée selon la distance, le volume, les accès, la manutention et les possibilités de groupage.
          </p>
          <div className="grid sm:grid-cols-2 gap-2.5 mb-8">
            {LONG_DISTANCE_ADVANTAGES.map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm font-medium text-navy">
                <CheckCircle2 className="w-4 h-4 text-orange shrink-0" /> {item}
              </div>
            ))}
          </div>

          <h3 className="font-heading font-semibold text-lg text-navy mb-3">Destinations fréquemment desservies</h3>
          <p className="text-sm text-muted-foreground mb-3">Une autre destination ? Demandez votre trajet.</p>
          <div className="flex flex-wrap gap-2 mb-5" data-testid="frequent-destinations">
            {FREQUENT_DESTINATIONS.map((dest) =>
              dest.href ? (
                <Link
                  key={dest.label}
                  to={dest.href}
                  data-testid={`destination-${dest.label}`}
                  className="text-sm font-medium text-navy bg-graylight border border-gray-200 rounded-full px-4 py-1.5 hover:border-orange hover:text-orange transition-colors"
                >
                  {dest.label}
                </Link>
              ) : (
                <button
                  key={dest.label}
                  type="button"
                  data-testid={`destination-${dest.label}`}
                  onClick={() => requestEstimate({ type: "Longue distance", source: "longue-distance-destination" })}
                  className="text-sm font-medium text-navy bg-graylight border border-gray-200 rounded-full px-4 py-1.5 hover:border-orange hover:text-orange transition-colors"
                >
                  {dest.label}
                </button>
              )
            )}
          </div>

          <Button
            data-testid="long-distance-cta"
            onClick={() => requestEstimate({ type: "Longue distance", source: "longue-distance-cta" })}
            className="bg-orange hover:bg-orange-hover text-white rounded-full px-7 py-6 font-semibold transition-transform hover:-translate-y-0.5 shadow-lg shadow-orange/20"
          >
            Obtenir mon estimation <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl overflow-hidden border border-gray-200"
        >
          <img
            src={LONG_DISTANCE_VISUAL}
            alt="Transport longue distance en véhicule utilitaire depuis Le Havre et la Normandie"
            loading="lazy"
            className="w-full h-[300px] md:h-[480px] object-cover"
            data-testid="long-distance-image"
          />
        </motion.div>
      </div>
    </section>
  );
}
